import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { getAllUnified, searchContent } from "@/utils/content";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const RECOMMEND_COUNT = 4;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);

  useDocumentTitle({
    title: keyword ? `搜索「${keyword}」 - 我妻子的一切` : "搜索 - 我妻子的一切",
    description: "搜索《我妻子的一切》的剧情介绍、角色档案与影视资讯，快速找到你感兴趣的内容。",
    keywords: "搜索,我妻子的一切,剧情,角色,资讯",
  });

  const results = useMemo(() => (keyword ? searchContent(keyword) : []), [keyword]);
  const recommendations = useMemo(() => getAllUnified().slice(0, RECOMMEND_COUNT), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (input.trim()) {
      next.set("q", input.trim());
    } else {
      next.delete("q");
    }
    setParams(next);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <header className="mt-5 max-w-3xl">
        <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">搜索内容</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          支持按标题与正文全文匹配，覆盖剧情介绍、角色档案与影视资讯三类内容。
        </p>
      </header>

      <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
          <input
            type="search"
            name="q"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入关键词，如「婚姻」「素妍」「结局」"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-700 px-6 text-sm text-background-50 transition-colors hover:bg-primary-800 cursor-pointer"
        >
          <i className="ri-search-line" />
          搜索
        </button>
      </form>

      {keyword && (
        <p className="mt-6 text-sm text-foreground-500">
          关键词「<span className="font-medium text-primary-700">{keyword}</span>」共找到{" "}
          <span className="font-medium text-primary-700">{results.length}</span> 条相关内容
        </p>
      )}

      {keyword ? (
        results.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-5">
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到相关内容"
              description={`没有与「${keyword}」匹配的内容，换个关键词试试，或看看下面的推荐内容。`}
            />
            <div className="mt-8">
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">为你推荐</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {recommendations.map((item) => (
                  <ContentCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="mt-6">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="试试搜索「婚姻」「结局」「素妍」「幕后」等关键词，快速定位你想了解的内容。"
          />
          <div className="mt-8">
            <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">热门内容</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {recommendations.map((item) => (
                <ContentCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3 text-sm">
        <Link to="/category/drama" className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900">
          <i className="ri-book-open-line" />
          前往剧情分类
        </Link>
        <Link to="/category/characters" className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900">
          <i className="ri-user-3-line" />
          前往角色分类
        </Link>
        <Link to="/category/news" className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900">
          <i className="ri-newspaper-line" />
          前往资讯分类
        </Link>
      </div>
    </div>
  );
}